import { MessageListItem } from '../types';
import { i18n } from '@/plugins/i18n';
import { store } from '@/store';
import { UserState } from '@/store/user';
import { QueuedMessage } from './ChatFramework.types';
import { spawnSystemMessage } from '../lib';
import { MessageType } from '../enum';
import Api from '@/common/api';
import { transactionParamsSwitch, betsTypeSwitch } from '@/common/sb-transaction.helpers';
import { ExternalTransactionType, ExternalTransactionKind } from '../enum/ExternalTransactionType';
import { toArr } from './ChatFramework.utils';
import { ArticleAutocompleteState } from '@/store/articleAutocomplete/model';
import { PredictiveSuggestionConfig } from '@/common/api/models.pleased';
import { PREDICTIVE_SUGGESTION } from './ChatMessagePredictiveSuggestions.lib';
import { logger } from '@/common/api/logger';
import {
  captureEventException,
  ErrorTrackingEvent,
  isAxiosErrorLike,
  isCancelledRequest,
} from '@/common/error-tracking';

const getState = () => store.state as { user: UserState; articleAutoComplete: ArticleAutocompleteState };
const getTitleParam = () => ({ botName: getState()?.articleAutoComplete?.widgetTitle || 'Help Assistant' });

/** Sporty history endpoints used by predictive suggestion checks. */
type PredictiveSuggestionEndpoint = 'statements' | 'realbetlist';

type PredictiveSuggestionFailureContext = {
  endpoint: PredictiveSuggestionEndpoint;
  predictionId: string;
  bizCode?: number | string;
};

const getAuthDiagnostics = () => {
  const { user } = getState();
  return {
    supportJweToken: Boolean(user?.supportJweToken),
    isLogin: Boolean(user?.isLogin),
  };
};

const reportPredictiveSuggestionError = (error: unknown, context: PredictiveSuggestionFailureContext): void => {
  // Network failures on sbAxios are already reported by attachAxiosErrorTracking.
  if (isCancelledRequest(error) || isAxiosErrorLike(error)) return;

  const auth = getAuthDiagnostics();
  const bizCode = context.bizCode ?? (error as { bizCode?: number | string } | null)?.bizCode;

  captureEventException(error, ErrorTrackingEvent.PredictiveSuggestion, {
    tags: {
      prediction_id: context.predictionId,
      sb_endpoint: context.endpoint,
      api_code: bizCode != null ? String(bizCode) : undefined,
      support_jwe_token: String(auth.supportJweToken),
      vuex_is_login: String(auth.isLogin),
    },
  });
};

const throwCannotGetTransactions = (context: PredictiveSuggestionFailureContext): never => {
  const message =
    context.bizCode != null ? `Cannot get transactions (bizCode ${context.bizCode})` : 'Cannot get transactions';
  throw Object.assign(new Error(message), { bizCode: context.bizCode });
};

// Sequence
// 1. Get user foreign id.
// Predictions
type Prediction = {
  id: string;
  ui: () => string;
  run(config?: PredictiveSuggestionConfig): QueuedMessage;
  selectionId: number;
};

const greylisted: Prediction = {
  id: PREDICTIVE_SUGGESTION.USER_ACCOUNT_GREY_LISTED,
  selectionId: 1,
  // IMPORTANT:
  // We can't show direct "Greylisted" reason to user, that's why we use "Failed Withdrawal" here
  // It's not an issue, expected behaviour
  ui: () => i18n.t('module.suggestion.response.withdrawal_issue') as string,
  async run(config) {
    const vuexState = getState();
    const { user } = vuexState;
    const isAccountGreyListed = Boolean(user.greyListed || user.pleasedUser?.greyListed);
    if (!isAccountGreyListed) return [];

    logger.debug('[CHAT_MESSAGE_PREDICTIVE_SUGGESTIONS] User is greylisted', { user });

    return spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PREDICTION,
      // Same with picked i18n key here, as explained
      message: config?.message || (i18n.t('module.suggestion.withdrawal_issue', getTitleParam()) as string),
      config: {
        connect: {
          liveChat: Boolean(config?.connectToAgent && config?.showLiveChat),
          loginRequired: true,
          openRequest: Boolean(config?.connectToAgent && config?.showOpenRequest),
          voiceChat: Boolean(config?.connectToAgent && config?.showVoiceChat),
          showDescriptionBox: true,
          connect: Boolean(config?.connectToAgent),
          redirectToArticleId: config?.articleId,
          transactionType: ExternalTransactionType.WITHDRAWALS_PENDING,
        },
        matchedPredictiveOption: greylisted.id,
      },
    });
  },
};

const failedDeposit: Prediction = {
  id: PREDICTIVE_SUGGESTION.FAILED_DEPOSIT_IN_THE_PAST_6_HOURS,
  selectionId: 2,
  ui: () => i18n.t('module.suggestion.response.deposit_issue') as string,
  async run(config) {
    const params = transactionParamsSwitch.match(ExternalTransactionType.DEPOSITS_FAILED);
    const failureContext: PredictiveSuggestionFailureContext = {
      endpoint: 'statements',
      predictionId: failedDeposit.id,
    };
    try {
      const res = await Api.getTransactionHistory({
        ...params,
        pageSize: 5,
        createTimeStart: getHourAgoTimestamp(6),
        _t: +new Date(),
      });
      if (res.bizCode !== 10000) {
        failureContext.bizCode = res.bizCode;
        throwCannotGetTransactions(failureContext);
      }
      if (!res.data.statements.length) {
        return [];
      }

      return spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PREDICTION,
        message: config?.message || (i18n.t('module.suggestion.deposit_issue', getTitleParam()) as string),
        config: {
          connect: {
            liveChat: Boolean(config?.connectToAgent && config?.showLiveChat),
            loginRequired: true,
            openRequest: Boolean(config?.connectToAgent && config?.showOpenRequest),
            voiceChat: Boolean(config?.connectToAgent && config?.showVoiceChat),
            showDescriptionBox: true,
            connect: Boolean(config?.connectToAgent),
            redirectToArticleId: config?.articleId,
            transactionType: ExternalTransactionType.DEPOSITS_FAILED,
          },
          matchedPredictiveOption: failedDeposit.id,
        },
      });
    } catch (e) {
      reportPredictiveSuggestionError(e, failureContext);
      return [
        /* Gracefully ignore the error */
      ];
    }
  },
};

const failedWithdrawals: Prediction = {
  id: PREDICTIVE_SUGGESTION.FAILED_WITHDRAWS_IN_THE_PAST_6_HOURS,
  selectionId: 3,
  ui: () => i18n.t('module.suggestion.response.withdrawal_issue') as string,
  async run(config) {
    const params = transactionParamsSwitch.match(ExternalTransactionType.WITHDRAWALS_FAILED);
    const failureContext: PredictiveSuggestionFailureContext = {
      endpoint: 'statements',
      predictionId: failedWithdrawals.id,
    };
    try {
      const res = await Api.getTransactionHistory({
        ...params,
        pageSize: 5,
        createTimeStart: getHourAgoTimestamp(6),
        _t: +new Date(),
      });
      if (res.bizCode !== 10000) {
        failureContext.bizCode = res.bizCode;
        throwCannotGetTransactions(failureContext);
      }
      if (!res.data.statements.length) {
        return [];
      }

      return spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PREDICTION,
        message: config?.message || (i18n.t('module.suggestion.withdrawal_issue', getTitleParam()) as string),
        config: {
          connect: {
            liveChat: Boolean(config?.connectToAgent && config?.showLiveChat),
            loginRequired: true,
            openRequest: Boolean(config?.connectToAgent && config?.showOpenRequest),
            voiceChat: Boolean(config?.connectToAgent && config?.showVoiceChat),
            showDescriptionBox: true,
            connect: Boolean(config?.connectToAgent),
            redirectToArticleId: config?.articleId,
            transactionType: ExternalTransactionType.WITHDRAWALS_FAILED,
          },
          matchedPredictiveOption: failedWithdrawals.id,
        },
      });
    } catch (e) {
      reportPredictiveSuggestionError(e, failureContext);
      return [
        /* Gracefully ignore the error */
      ];
    }
  },
};

const checkBetType = async (type: ExternalTransactionKind, predictionId: string) => {
  const winningStatus = betsTypeSwitch.match(type);
  const failureContext: PredictiveSuggestionFailureContext = {
    endpoint: 'realbetlist',
    predictionId,
  };
  try {
    const res = await Api.getBetHistory({
      // add createdStart.
      winningStatus,
      pageSize: 5,
      startTime: getHourAgoTimestamp(6),
      _t: +new Date(),
    });
    if (res.bizCode !== 10000) {
      failureContext.bizCode = res.bizCode;
      throwCannotGetTransactions(failureContext);
    }
    return res.data.entityList;
  } catch (e) {
    reportPredictiveSuggestionError(e, failureContext);
    return [];
  }
};

const lostBets: Prediction = {
  id: PREDICTIVE_SUGGESTION.LOST_BET_IN_THE_PAST_6_HOURS,
  selectionId: 4,
  ui: () => i18n.t('module.suggestion.response.lost_bet_issue') as string,
  async run(config) {
    const bets = await checkBetType(ExternalTransactionType.BETS_LOST, lostBets.id);
    if (!bets.length) {
      return [];
    }

    return spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PREDICTION,
      message: config?.message || (i18n.t('module.suggestion.lost_bet_issue', getTitleParam()) as string),
      config: {
        connect: {
          liveChat: Boolean(config?.connectToAgent && config?.showLiveChat),
          loginRequired: true,
          openRequest: Boolean(config?.connectToAgent && config?.showOpenRequest),
          voiceChat: Boolean(config?.connectToAgent && config?.showVoiceChat),
          showDescriptionBox: true,
          connect: Boolean(config?.connectToAgent),
          redirectToArticleId: config?.articleId,
          transactionType: ExternalTransactionType.BETS_LOST,
        },
        matchedPredictiveOption: lostBets.id,
      },
    });
  },
};

const unsettledBets: Prediction = {
  id: PREDICTIVE_SUGGESTION.UNSETTLED_BET_IN_THE_PAST_6_HOURS,
  selectionId: 5,
  ui: () => i18n.t('module.suggestion.response.unsettled_bet_issue') as string,
  async run(config) {
    const bets = await checkBetType(ExternalTransactionType.BETS_UNSETTLED, unsettledBets.id);
    if (!bets.length) return [];

    return spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PREDICTION,
      message: config?.message || (i18n.t('module.suggestion.unsettled_bet_issue', getTitleParam()) as string),
      config: {
        connect: {
          liveChat: Boolean(config?.connectToAgent && config?.showLiveChat),
          loginRequired: true,
          openRequest: Boolean(config?.connectToAgent && config?.showOpenRequest),
          voiceChat: Boolean(config?.connectToAgent && config?.showVoiceChat),
          showDescriptionBox: true,
          connect: Boolean(config?.connectToAgent),
          redirectToArticleId: config?.articleId,
          transactionType: ExternalTransactionType.BETS_UNSETTLED,
        },
        matchedPredictiveOption: unsettledBets.id,
      },
    });
  },
};

const voidedBets: Prediction = {
  id: PREDICTIVE_SUGGESTION.VOIDED_BET_IN_THE_PAST_6_HOURS,
  selectionId: 6,
  ui: () => i18n.t('module.suggestion.response.voided_bet_issue') as string,
  async run(config) {
    const bets = await checkBetType(ExternalTransactionType.BETS_VOIDED, voidedBets.id);
    if (!bets.length) return [];

    return spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PREDICTION,
      message: config?.message || (i18n.t('module.suggestion.voided_bet_issue', getTitleParam()) as string),
      config: {
        connect: {
          liveChat: Boolean(config?.connectToAgent && config?.showLiveChat),
          loginRequired: true,
          openRequest: Boolean(config?.connectToAgent && config?.showOpenRequest),
          voiceChat: Boolean(config?.connectToAgent && config?.showVoiceChat),
          showDescriptionBox: true,
          connect: Boolean(config?.connectToAgent),
          redirectToArticleId: config?.articleId,
          transactionType: ExternalTransactionType.BETS_VOIDED,
        },
        matchedPredictiveOption: voidedBets.id,
      },
    });
  },
};

export async function resolvePredictiveMessages(configuration?: null | PredictiveSuggestionConfig[]) {
  if (!configuration?.length) return [];
  const SEQUENCE = [[greylisted], [failedDeposit, failedWithdrawals], [lostBets, unsettledBets, voidedBets]].map(
    (list) =>
      list.map((prediction) => ({
        prediction,
        config: configuration?.find((c) => c.predictiveSuggestion === prediction.id && c.isActive),
      }))
  );

  for (const parallels of SEQUENCE) {
    const found = await Promise.all(
      parallels.filter((p) => p.config).map((p) => Promise.resolve(p.prediction.run(p.config)))
    ).then((r) => r.find((res) => toArr(res).length));
    if (found) return found as MessageListItem | MessageListItem[];
  }
  return [];
}

export function getPredictionById(id: string): Prediction | undefined {
  return [greylisted, failedDeposit, failedWithdrawals, lostBets, unsettledBets, voidedBets].find((p) => p.id === id);
}

function getHourAgoTimestamp(hours = 6) {
  // Get the current time in milliseconds
  const currentTime = Date.now();

  // Subtract 6 hours (6 * 60 * 60 * 1000 milliseconds)
  const sixHoursInMillis = hours * 60 * 60 * 1000;
  const pastTimestamp = currentTime - sixHoursInMillis;
  return pastTimestamp;
}
